import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { EditableQA } from "@/components/admin/EditableQA";
import { UserReviewsSection } from "@/components/reviews/UserReviewsSection";

interface ProductContentTabsProps {
  product: any;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  onUpdateQA: (qaItems: { question: string; answer: string }[]) => Promise<void>;
  onGenerateQA: () => void;
  isGenerating: boolean;
  isEditor: boolean;
  isEditMode: boolean;
}

export function ProductContentTabs({
  product,
  activeTab,
  setActiveTab,
  searchQuery,
  setSearchQuery,
  onUpdateQA,
  onGenerateQA,
  isGenerating,
  isEditor,
  isEditMode,
}: ProductContentTabsProps) {
  return (
    <div id="faqs" className="mb-8 md:mb-16">
      <h2 className="text-xl md:text-3xl font-bold mb-4 md:mb-8">Reviews & Questions</h2>

      {/* Search Bar */}
      <div className="mb-4 md:mb-6">
        <div className="relative max-w-2xl mx-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3 md:h-4 w-3 md:w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search reviews and questions"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 md:pl-10 rounded-full text-sm md:text-base h-9 md:h-10"
          />
        </div>
        {searchQuery && (
          <p className="text-center text-xs md:text-sm text-muted-foreground mt-2">
            Showing results for "{searchQuery}"
          </p>
        )}
      </div>

      {/* Tabs Section */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" id="reviews-section">
        <TabsList className="w-full justify-start border-b rounded-none h-auto p-0 bg-transparent mb-4 md:mb-6">
          <TabsTrigger
            value="reviews"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent text-xs md:text-sm px-3 md:px-4 py-2"
          >
            Reviews <Badge variant="secondary" className="ml-1 md:ml-2 text-xs">{product?.reviews?.length || 0}</Badge>
          </TabsTrigger>
          <TabsTrigger
            value="faqs"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent text-xs md:text-sm px-3 md:px-4 py-2"
          >
            FAQs <Badge variant="secondary" className="ml-1 md:ml-2 text-xs">{product.decision_qa?.length || 0}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* Reviews Tab */}
        <TabsContent value="reviews" className="mt-3 md:mt-6" id="reviews">
          <UserReviewsSection productId={product.id} productTitle={product.title} />
        </TabsContent>

        {/* FAQs Tab */}
        <TabsContent value="faqs" className="mt-6">
          <EditableQA
            items={product.decision_qa || []}
            onSave={onUpdateQA}
            onGenerate={onGenerateQA}
            isGenerating={isGenerating}
          />
        </TabsContent>

      </Tabs>
    </div>
  );
}
